import { useState } from 'react';
import { Check } from 'lucide-react';
import { colors, accent } from '@/lib/tokens';
import { submitBugReport, type BugArea, type BugSeverity } from '@/lib/api-client';
import { CustomDropdown } from '@/components/ui/custom-dropdown';
import { redactSensitiveUrl } from '@/lib/utils';

export interface BugReportFormProps {
  defaultArea?: BugArea;
  defaultUrl?: string;
  hideAreaField?: boolean;
  onSuccess?: () => void;
  compact?: boolean;
}

const SEVERITY_OPTIONS: { value: BugSeverity; label: string }[] = [
  { value: 'blocker', label: "Blocker - can't use the product" },
  { value: 'major', label: 'Major - significantly broken' },
  { value: 'minor', label: "Minor - something's off" },
  { value: 'nitpick', label: 'Nitpick - polish / suggestion' },
];

const AREA_OPTIONS: { value: BugArea; label: string }[] = [
  { value: 'sdk', label: 'SDK' },
  { value: 'vscode', label: 'VS Code extension' },
  { value: 'mcp', label: 'MCP server' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'landing', label: 'Landing page' },
  { value: 'docs', label: 'Docs' },
  { value: 'other', label: 'Other' },
];

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="block text-sm font-medium mb-1.5" style={{ color: colors.textSecondary }}>{children}</label>;
}

export default function BugReportForm({
  defaultArea = 'dashboard',
  defaultUrl,
  hideAreaField = false,
  onSuccess,
  compact = false,
}: BugReportFormProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<BugSeverity>('minor');
  const [area, setArea] = useState<BugArea>(defaultArea);
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const gap = compact ? 'mb-3' : 'mb-4';
  const inputClass = 'w-full px-4 py-2.5 rounded-md text-sm placeholder-[#a8a29e] focus:outline-none transition-all';
  const inputStyle = { background: colors.bgBase, border: `1px solid ${colors.borderDefault}`, color: colors.textPrimary };

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      setError('Please add a title and description.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await submitBugReport({
        title: title.trim(),
        description: description.trim(),
        severity,
        area,
        email: email.trim() || undefined,
        website: website.trim(),
        url: redactSensitiveUrl(defaultUrl || window.location.href),
        referrer: redactSensitiveUrl(document.referrer),
        user_agent: navigator.userAgent,
      });
      setSubmitted(true);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full" style={{ background: colors.accentSubtle }}>
          <Check className="h-6 w-6" style={{ color: accent }} />
        </div>
        <h3 className="text-base font-semibold" style={{ color: colors.textPrimary }}>Thanks for the feedback</h3>
        <p className="mt-1 text-sm" style={{ color: colors.textMuted }}>We read every report and will take a look soon.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={gap}>
        <FieldLabel>Title</FieldLabel>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Short summary of the issue"
          className={inputClass}
          style={inputStyle}
        />
      </div>

      <div className={gap}>
        <FieldLabel>Description</FieldLabel>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={compact ? 4 : 5}
          placeholder="What happened? What did you expect?"
          className={`${inputClass} resize-none`}
          style={inputStyle}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel>Severity</FieldLabel>
          <CustomDropdown
            value={severity}
            onChange={(next) => setSeverity(next as BugSeverity)}
            options={SEVERITY_OPTIONS}
            placeholder="Select severity"
            ariaLabel="Severity"
            name="severity"
          />
        </div>

        {!hideAreaField && (
          <div>
            <FieldLabel>Area</FieldLabel>
            <CustomDropdown
              value={area}
              onChange={(next) => setArea(next as BugArea)}
              options={AREA_OPTIONS}
              placeholder="Select area"
              ariaLabel="Area"
              name="area"
            />
          </div>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel>Email</FieldLabel>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="you@example.com"
            className={inputClass}
            style={inputStyle}
          />
        </div>

        <div>
          <FieldLabel>Website</FieldLabel>
          <input
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            type="text"
            placeholder="Optional"
            className={inputClass}
            style={inputStyle}
          />
        </div>
      </div>

      {error && <p className="text-sm" style={{ color: colors.error }}>{error}</p>}

      <button
        type="submit"
        disabled={submitting}
        className="h-11 w-full rounded-md font-medium transition-opacity disabled:opacity-60"
        style={{ background: accent, color: '#ffffff' }}
      >
        {submitting ? 'Submitting...' : 'Submit feedback'}
      </button>
    </form>
  );
}
